import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Alert, Fade, Chip, Button,
  Skeleton, useTheme, alpha, Tooltip, IconButton,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  AccessTime as AccessTimeIcon,
  CheckCircle as CheckCircleIcon,
  HourglassBottom as HourglassIcon,
  Groups as GroupsIcon,
  LocalHospital as LocalHospitalIcon,
  MedicalServices as MedicalServicesIcon,
} from '@mui/icons-material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  ResponsiveContainer, Cell, AreaChart, Area,
} from 'recharts';
import { useAuth } from '../../contexts/AuthContext';
import { useUnidade } from '../../contexts/UnidadeContext';
import { saudeService } from '../../services/api';
import PainelNaoConfigurado from '../../components/saude/PainelNaoConfigurado';

// ─── Tipos ───

interface Totais {
  total: number;
  finalizados: number;
  em_andamento: number;
  profissionais: number;
  unidades_ativas: number;
  especialidades: number;
}

interface PorUnidade {
  codunidade: string;
  nome: string;
  total: number;
  finalizados: number;
}

interface PorHora {
  hora: string;
  total: number;
}

interface PorEspecialidade {
  especialidade: string;
  total: number;
}

interface Atendimentos24h {
  totais: Totais;
  por_unidade: PorUnidade[];
  por_hora: PorHora[];
  por_especialidade: PorEspecialidade[];
  gerado_em: string;
}

const REFRESH_MS = 60_000;
const BAR_COLORS = ['#1976d2', '#43a047', '#ff9800', '#7b1fa2', '#00838f', '#e53935', '#6d4c41', '#546e7a'];

// ─── KPI Card ───

const KpiCard: React.FC<{
  label: string; value: number; icon: React.ReactNode; color: string; loading: boolean;
}> = ({ label, value, icon, color, loading }) => {
  const theme = useTheme();
  return (
    <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{
          width: 48, height: 48, borderRadius: 2, display: 'flex', alignItems: 'center',
          justifyContent: 'center', bgcolor: alpha(color, 0.12), color,
        }}>
          {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          {loading
            ? <Skeleton width={56} height={36} />
            : <Typography variant="h4" fontWeight={700} sx={{ lineHeight: 1 }}>{value.toLocaleString('pt-BR')}</Typography>}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>{label}</Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

// ─── Pagina ───

const SaudeAtendimentos24h: React.FC = () => {
  const theme = useTheme();
  const { municipioAtivo } = useAuth();
  const { unidadeSelecionada } = useUnidade();

  const [data, setData] = useState<Atendimentos24h | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [naoConfigurado, setNaoConfigurado] = useState(false);
  const [ultimaAtualizacao, setUltimaAtualizacao] = useState<Date | null>(null);
  const [segundos, setSegundos] = useState(REFRESH_MS / 1000);

  // mantem a referencia da funcao de fetch atualizada sem reiniciar os timers
  const fetchRef = useRef<() => void>(() => {});

  const fetchData = useCallback(async (silencioso = false) => {
    if (!municipioAtivo?.id) return;
    if (!silencioso) setLoading(true);
    setError(null);
    try {
      const result = await saudeService.getAtendimentos24h(municipioAtivo.id, unidadeSelecionada?.codunidade);
      setNaoConfigurado(result?.configurado === false);
      setData(result);
      setUltimaAtualizacao(new Date());
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Erro ao carregar atendimentos das ultimas 24 horas.');
    } finally {
      setLoading(false);
      setSegundos(REFRESH_MS / 1000);
    }
  }, [municipioAtivo?.id, unidadeSelecionada?.codunidade]);

  useEffect(() => { fetchRef.current = () => fetchData(true); });

  // Busca inicial / ao trocar municipio ou unidade
  useEffect(() => { fetchData(false); }, [fetchData]);

  // Auto-refresh a cada 60s (silencioso) + contador regressivo
  useEffect(() => {
    if (!municipioAtivo?.id) return;
    const refresh = setInterval(() => fetchRef.current(), REFRESH_MS);
    const tick = setInterval(() => setSegundos((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => { clearInterval(refresh); clearInterval(tick); };
  }, [municipioAtivo?.id]);

  const totais = data?.totais;

  const horaData = useMemo(() => {
    return (data?.por_hora || []).map((h) => ({
      ...h,
      label: new Date(h.hora).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
    }));
  }, [data?.por_hora]);

  if (!municipioAtivo?.id) {
    return <Box sx={{ p: 3 }}><Alert severity="info">Selecione um municipio para visualizar os atendimentos.</Alert></Box>;
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Cabecalho */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AccessTimeIcon color="primary" /> Atendimentos nas ultimas 24 horas
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {unidadeSelecionada
              ? <>Unidade: <strong>{unidadeSelecionada.nome}</strong></>
              : 'Todas as unidades do municipio'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {ultimaAtualizacao
                ? `Atualizado as ${ultimaAtualizacao.toLocaleTimeString('pt-BR')}`
                : 'Carregando...'}
            </Typography>
            <Typography variant="caption" color="text.disabled">
              Proxima atualizacao em {segundos}s
            </Typography>
          </Box>
          <Tooltip title="Atualizar agora">
            <span>
              <IconButton onClick={() => fetchData(false)} disabled={loading} color="primary"
                sx={{ border: `1px solid ${theme.palette.divider}` }}>
                <RefreshIcon sx={{ animation: loading ? 'spin 1s linear infinite' : 'none',
                  '@keyframes spin': { '100%': { transform: 'rotate(360deg)' } } }} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {naoConfigurado && <PainelNaoConfigurado />}
      {error && <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={() => fetchData(false)}>Tentar novamente</Button>}>{error}</Alert>}

      {/* KPIs */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={2.4}><KpiCard label="Atendimentos (24h)" value={totais?.total ?? 0} icon={<LocalHospitalIcon />} color="#1976d2" loading={loading} /></Grid>
        <Grid item xs={6} md={2.4}><KpiCard label="Finalizados" value={totais?.finalizados ?? 0} icon={<CheckCircleIcon />} color="#43a047" loading={loading} /></Grid>
        <Grid item xs={6} md={2.4}><KpiCard label="Em andamento" value={totais?.em_andamento ?? 0} icon={<HourglassIcon />} color="#ff9800" loading={loading} /></Grid>
        <Grid item xs={6} md={2.4}><KpiCard label="Profissionais" value={totais?.profissionais ?? 0} icon={<GroupsIcon />} color="#7b1fa2" loading={loading} /></Grid>
        <Grid item xs={6} md={2.4}><KpiCard label="Unidades ativas" value={totais?.unidades_ativas ?? 0} icon={<MedicalServicesIcon />} color="#00838f" loading={loading} /></Grid>
      </Grid>

      <Grid container spacing={2}>
        {/* Por unidade */}
        <Grid item xs={12} lg={7}>
          <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                Atendimentos por unidade {unidadeSelecionada ? '' : '(todas)'}
              </Typography>
              {loading ? <Skeleton variant="rectangular" height={320} /> : (
                <ResponsiveContainer width="100%" height={Math.max(320, (data?.por_unidade.length || 1) * 38)}>
                  <BarChart data={data?.por_unidade || []} layout="vertical" margin={{ top: 4, right: 32, bottom: 4, left: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={alpha(theme.palette.text.primary, 0.08)} />
                    <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="nome" width={200} tick={{ fontSize: 11 }} interval={0} />
                    <RechartsTooltip formatter={((v: any) => [`${v}`, 'Atendimentos']) as any} />
                    <Bar dataKey="total" radius={[0, 4, 4, 0]} label={{ position: 'right', fontSize: 11 }}>
                      {(data?.por_unidade || []).map((_, i) => <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Por especialidade */}
        <Grid item xs={12} lg={5}>
          <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>Top especialidades (24h)</Typography>
              {loading ? <Skeleton variant="rectangular" height={320} /> : (data?.por_especialidade.length ? (
                <ResponsiveContainer width="100%" height={Math.max(320, data.por_especialidade.length * 38)}>
                  <BarChart data={data.por_especialidade} layout="vertical" margin={{ top: 4, right: 32, bottom: 4, left: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={alpha(theme.palette.text.primary, 0.08)} />
                    <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="especialidade" width={150} tick={{ fontSize: 11 }} interval={0} />
                    <RechartsTooltip formatter={((v: any) => [`${v}`, 'Atendimentos']) as any} />
                    <Bar dataKey="total" radius={[0, 4, 4, 0]} fill={theme.palette.primary.main} label={{ position: 'right', fontSize: 11 }} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <Box sx={{ py: 6, textAlign: 'center' }}><Typography color="text.secondary">Sem atendimentos nas ultimas 24h.</Typography></Box>)}
            </CardContent>
          </Card>
        </Grid>

        {/* Por hora */}
        <Grid item xs={12}>
          <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}` }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>Distribuicao por hora (24h)</Typography>
              {loading ? <Skeleton variant="rectangular" height={240} /> : (horaData.length ? (
                <ResponsiveContainer width="100%" height={260}>
                  <AreaChart data={horaData} margin={{ top: 8, right: 24, bottom: 4, left: 0 }}>
                    <defs>
                      <linearGradient id="gradAtend" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={theme.palette.primary.main} stopOpacity={0.5} />
                        <stop offset="95%" stopColor={theme.palette.primary.main} stopOpacity={0.05} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.text.primary, 0.08)} />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                    <RechartsTooltip formatter={((v: any) => [`${v}`, 'Atendimentos']) as any} />
                    <Area type="monotone" dataKey="total" stroke={theme.palette.primary.main} strokeWidth={2} fill="url(#gradAtend)" />
                  </AreaChart>
                </ResponsiveContainer>
              ) : <Box sx={{ py: 6, textAlign: 'center' }}><Typography color="text.secondary">Sem atendimentos nas ultimas 24h.</Typography></Box>)}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Fade in={!loading}>
        <Chip size="small" variant="outlined" sx={{ mt: 2 }}
          label={`Janela: ultimas 24h • Fonte: atendimentos operacionais${unidadeSelecionada ? ` • ${unidadeSelecionada.nome}` : ''}`} />
      </Fade>
    </Box>
  );
};

export default SaudeAtendimentos24h;
